import { ReadingSkillJsonItem } from "types/contentful";
import { ReadingLevelImageMap } from "utils/readingLevelImageUtils";
import { formatContentfulImageForNextImage } from "utils/contentful/helpers";

export interface GetReadingLevelMetadataArgs {
  prometheusAge: string | number;
  readingLevels: ReadingSkillJsonItem[];
}

/**
 * Gets reading skills metadata related to a reading level category.
 *
 * @param {Object} args
 * @param {String|Number} args.prometheusAge
 * @param {Array} args.readingLevels
 */
export function getReadingLevelMetadata(args: GetReadingLevelMetadataArgs) {
  if (!args?.prometheusAge || !args?.readingLevels) {
    return null;
  }

  const { prometheusAge, readingLevels } = args;

  const promAge = Number(prometheusAge);
  if (isNaN(promAge)) {
    return null;
  }

  const rlLength = readingLevels.length;
  if (!rlLength) {
    return null;
  }

  const ageKey = "prometheus-age";

  // less than minimum defined age mapping, choose first category
  if (promAge < Number(readingLevels[0]?.[ageKey])) {
    return readingLevels[0];
  }

  // greater than max defined age mapping, choose last category
  if (promAge >= Number(readingLevels[rlLength - 1]?.[ageKey])) {
    return readingLevels[rlLength - 1];
  }

  let readingLevel = null;
  for (let i = 0; i < rlLength - 1; ++i) {
    const curr = readingLevels[i];
    const next = readingLevels[i + 1];

    if (promAge >= Number(curr?.[ageKey]) && promAge < Number(next?.[ageKey])) {
      readingLevel = readingLevels[i];
    }
  }

  return readingLevel;
}

export interface GetImagePropsForReadingLevelArgs {
  readingLevelName: string;
  readingLevelImages: ReadingLevelImageMap;
}

export function getImagePropsForReadingLevel({
  readingLevelName,
  readingLevelImages,
}: GetImagePropsForReadingLevelArgs) {
  if (!readingLevelName || !readingLevelImages) {
    return null;
  }

  const readingLevelImageRaw =
    readingLevelImages[readingLevelName.toLowerCase()];

  const readingLevelImageProps = readingLevelImageRaw
    ? formatContentfulImageForNextImage(readingLevelImageRaw.fields)
    : null;

  return readingLevelImageProps;
}
